// DesktopLayout.js
// Matches the Twenty4Seven brand mockup ("1b — Peplink device detail") exactly for
// the core sections, and extends the same dark/mono/square-edge design language to
// the sections your app has that the mockup didn't show (client list, WAN traffic
// distribution, ping monitoring, real-time bandwidth, location map).
import React, { useState } from "react";
import FleetHeader from "../components/FleetHeader";
import DateSelector from "../components/DateSelector";
import PDFDownloadButton from "../components/PDFDownloadButton";
import RealtimeBandwidthChart from "../components/monitoring/RealtimeBandwidthChart";
import SpeedFusionLiveStatus from "../components/monitoring/SpeedFusionLiveStatus";
import LeafletMap, { hasLocationTrack } from "../components/monitoring/LeafletMap";
import CombinedHourlyGraph from "../components/monitoring/CombinedHourlyGraph";
import DailyBreakdownChart from "../components/monitoring/DailyBreakdownChart";
import T247WanModal from "../components/monitoring/T247WanModal";
import WanPriorityControl from "../components/monitoring/WanPriorityControl";
import { canChangePriorityForType, applyPendingToReport } from "../utils/wanPriority";
import T247VlanModal from "../components/monitoring/T247VlanModal";
import LogsDisplay from "../components/monitoring/LogsDisplay";

import {
  T247_THEME_CSS,
  buildDeviceData,
  computeWanRows,
  computeVlanRows,
  computeThroughput,
  computePingStats,
  fmt1,
  fmt2,
  safeArr,
  extractLogText,
  DualAreaSpark,
  AreaSpark,
  MiniBar,
  DonutChart,
} from "../components/monitoring/t247Shared";
import { useNavigate } from "react-router-dom";

function DesktopLayout({
  user,
  userType,
  isClient,
  serialNumber,
  selectedDate,
  setSelectedDate,
  period = "daily",
  setPeriod,
  deviceData: rawDeviceData,
  isLoading,
  lastUpdated,
  selectedDeviceId,
  alerts,
  vlanAlerts,
  wanCapAlerts,
  starlinkAlerts,
  isLoadingAlerts,
  isReportReady,
  shouldSkipStarlink,
  onLogout,
  managementType,
  ic2Writable,
  onPriorityChanged,
  starlinkLocation,
}) {
  const loadingVideo = process.env.PUBLIC_URL + '/Logo_video.mp4';
  const navigate = useNavigate();
  // Back to the fleet overview: clients -> ClientSiteDashboard (/dashboard),
  // admins -> SiteDashboard (/admin/dashboard). Same routes as App.jsx.
  const backPath = userType === "client" ? "/dashboard" : "/admin/dashboard";
  const goBack = () => navigate(backPath);
  const [selectedWan, setSelectedWan] = useState(null);
  const [selectedVlan, setSelectedVlan] = useState(null);
  const [clientSortDesc, setClientSortDesc] = useState(true);

  const deviceData = buildDeviceData(rawDeviceData);

  // ── WAN priority change ─────────────────────────────────────────────────
  // Only internal admins and client admins (role === 'admin' - same rule as
  // SiteDashboard/ClientSiteDashboard canEdit; the backend enforces the same),
  // only on the daily view of TODAY (older dates / weekly / monthly are
  // historical report data - view only), and only for device types the
  // shared wanPriority module allows (DC blocked unless its switch is on).
  const todayISO = new Date().toISOString().split("T")[0];
  const isLiveView = period === "daily" && selectedDate === todayISO;
  const canChangePriority = user?.role === "admin" && isLiveView && ic2Writable && canChangePriorityForType(managementType);
  const priorityScope = userType === "client" ? "client" : "admin";

  const isOnline = deviceData?.lastStatus === "online";
  const hasPeplinkTrack = hasLocationTrack(deviceData?.locationMap);

  // ── Derived data (shared with MobileLayout) ─────────────────────────────
  // Today's daily view shows just-submitted priority changes straight away
  // (same pending memory as LiveWanPanel). Historical views are untouched.
  const { rows: wanRows, totals: wanTotals, downCount } = computeWanRows(
    isLiveView ? applyPendingToReport(serialNumber, deviceData) : deviceData
  );
  const vlanRows = computeVlanRows(deviceData);
  const throughput = computeThroughput(deviceData, period);
  const pingStats = computePingStats(deviceData);

  const totalTrafficGB = wanTotals.total;
  const cellularGB = wanRows.filter((r) => r.isCellular).reduce((s, r) => s + r.total, 0);
  const cellularPct = totalTrafficGB > 0 ? (cellularGB / totalTrafficGB) * 100 : 0;
  const failoverEvents = safeArr(deviceData?.wanLogs).filter((l) => /fail|down/i.test(extractLogText(l))).length;
  const alertsCount = safeArr(alerts).length + safeArr(vlanAlerts).length + safeArr(wanCapAlerts).length + safeArr(starlinkAlerts).length;

  const allClientsSorted = deviceData
    ? [...safeArr(deviceData.clientUsages)].sort((a, b) => (clientSortDesc ? (b.total_usage || 0) - (a.total_usage || 0) : (a.total_usage || 0) - (b.total_usage || 0)))
    : [];

  const clientTotals = allClientsSorted.reduce(
    (acc, c) => ({
      download: acc.download + (c.download || 0) / 1024,
      upload: acc.upload + (c.upload || 0) / 1024,
      total: acc.total + (c.total_usage || 0) / 1024,
    }),
    { download: 0, upload: 0, total: 0 }
  );

  const wanDownloadSegments = wanRows.map((r) => ({ name: r.name, value: r.download }));
  const wanUploadSegments = wanRows.map((r) => ({ name: r.name, value: r.upload }));

  const formatLastOnline = (dateString) => {
    if (!dateString || dateString === "—") return "—";
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = date.toLocaleString('en', { month: 'short' });
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${day} ${month} ${hours}:${minutes}:${seconds}`;
    } catch {
      return dateString;
    }
  };

  return (
    <div className="t247">
      <FleetHeader title="Monitoring" user={user} onLogout={onLogout} scope={userType} />

      {/* ── DEVICE BANNER ───────────────────────────────────────────────── */}
      <div className="t247-devicebar">
        <div data-tour="mon-device">
          <button type="button" className="t247-back-btn" onClick={goBack} aria-label="Back to Site Dashboard">
            <i className="fas fa-arrow-left"></i>
            SITE DASHBOARD
          </button>
          <div className="t247-eyebrow">DEVICE</div>
          <div className="t247-devicename">
            {deviceData?.deviceName || serialNumber}
            {deviceData?.deviceName && serialNumber ? ` — ${serialNumber}` : ""}
          </div>
          <div className="t247-devicesub">
            {deviceData?.model && <span>{deviceData.model}</span>}
            {deviceData?.model && <span>·</span>}
            {deviceData?.firmwareVersion && <span>FW {deviceData.firmwareVersion}</span>}
            {deviceData?.firmwareVersion && <span>·</span>}
            <span>Last updated {lastUpdated || "—"}</span>
          </div>
          <span className={`t247-live-badge ${isOnline ? "" : "is-off"}`} style={{ marginTop: 10 }}>
            <span className="t247-dot" />
            {isOnline ? `ONLINE ${deviceData?.uptime || ""}` : "OFFLINE"}
          </span>
        </div>
        <div className="t247-devicebar-right">
          <div>
            <div className="t247-eyebrow">PERIOD</div>
            <div className="t247-period-toggle" data-tour="mon-period">
              {[
                { key: "daily", label: "DAILY" },
                { key: "weekly", label: "WEEKLY" },
                { key: "monthly", label: "MONTHLY" },
              ].map((p) => (
                <button key={p.key} type="button" className={`t247-period-btn ${period === p.key ? "active" : ""}`} onClick={() => setPeriod && setPeriod(p.key)}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="t247-datebtn-wrap" data-tour="mon-date">
            <DateSelector selectedDate={selectedDate} onDateChange={setSelectedDate} deviceId={selectedDeviceId} serialNumber={serialNumber} />
          </div>
          <div className="t247-pdf-wrap" data-tour="mon-pdf">
            <PDFDownloadButton
              deviceId={selectedDeviceId}
              period={period}
              date={selectedDate}
              deviceInfo={{ deviceName: deviceData?.deviceName, serialNumber: deviceData?.serialNumber }}
              isReportReady={isReportReady}
            />
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="cm-loading-overlay">
          <div className="cm-spinner-container">
            <video autoPlay loop muted playsInline preload="auto" poster={process.env.PUBLIC_URL + "/Logo_video_poster.jpg"} className="spinner-video" width="280" height="280">
              <source src={loadingVideo} type="video/mp4" />
            </video>
            <p>Loading device data…</p>
          </div>
        </div>
      )}

      {!isLoading && !deviceData && selectedDeviceId && (
        <div className="t247-error">
          No data available for this device. Please check the date selection.
        </div>
      )}

      {deviceData && !isLoading && (
        <>
          {/* ── DEVICE STATE + WAN TABLE (mockup core) ────────────────────── */}
          <div className="t247-columns">
            <div className="t247-leftcol" data-tour="mon-overview">
              <div className="t247-panel">
                <div className="t247-panel-header">DEVICE STATE</div>
                <div className="t247-state-grid">
                  <StateCell label="UPTIME" value={deviceData.uptime} />
                  <StateCell label="CLIENTS" value={String(deviceData.clientCount || 0)} />
                  <StateCell label="LAST ONLINE" value={deviceData.onlineAt ? formatLastOnline(deviceData.onlineAt) : "—"} small />
                  <StateCell label="LAST OFFLINE" value={deviceData.offlineAt ? formatLastOnline(deviceData.offlineAt) : "—"} small />
                  <StateCell label="MODEL" value={deviceData.model || "—"} small />
                  <StateCell label="FIRMWARE" value={deviceData.firmwareVersion || "—"} small />
                </div>
              </div>

              <div className="t247-panel">
                <div className="t247-panel-header">TODAY AT A GLANCE</div>
                <div className="t247-glance-list">
                  <GlanceRow label="Data through device" value={fmt2(totalTrafficGB)} unit="GB" />
                  <GlanceRow label="Carried on cellular" value={totalTrafficGB > 0 ? fmt1(cellularPct) : "—"} unit={totalTrafficGB > 0 ? "%" : ""} />
                  <GlanceRow label="Failover events" value={String(failoverEvents)} />
                </div>
              </div>
            </div>

            <div className="t247-panel" data-tour="mon-wan">
              <div className="t247-panel-header-row">
                <span>WAN STATUS &amp; USAGE</span>
                <span className="sub">{canChangePriority ? "CLICK A ROW FOR DETAIL · CLICK P▾ TO CHANGE PRIORITY" : "CLICK A ROW FOR DETAIL"}</span>
                <span className="t247-muted-label">
                  {wanRows.length} INTERFACE{wanRows.length === 1 ? "" : "S"} · {downCount} DOWN
                </span>
              </div>
              {wanRows.length === 0 ? (
                <EmptyState text="No WAN data for this period." />
              ) : (
                <>
                  <div className="t247-wan-headrow">
                    <div>INTERFACE</div>
                    <div style={{ textAlign: "right" }}>↓ GB</div>
                    <div style={{ textAlign: "right" }}>↑ GB</div>
                    <div style={{ textAlign: "right" }}>Σ GB</div>
                    <div>SHARE OF TRAFFIC</div>
                  </div>
                  <div className="t247-scroll-block" style={{ maxHeight: 320 }}>
                    {wanRows.map((r, idx) => (
                      <div className="t247-wan-row" key={r.key} onClick={() => setSelectedWan(r)} role="button" tabIndex={0} data-tour={idx === 0 ? "mon-first-wan" : undefined}>
                        <div className="t247-wan-name-cell">
                          <span className="t247-wan-dot" style={{ background: r.ledColor == 'red' ? "#D7D7CC" : r.ledColor == 'green' ? "#88F4EB" : "#CAFB6D" }} />
                          <div>
                            <div className="t247-wan-name">
                              {r.name} <span style={{textTransform: 'uppercase', }} className={`t247-chip ${r.ledColor == 'red' ? "chip-down" : r.ledColor == 'green' ? "chip-up" : "chip-other"}`}>{r.statusLabel}</span> <span data-tour={idx === 0 ? "mon-first-prio" : undefined}><WanPriorityControl
                                row={r}
                                serialNumber={serialNumber}
                                managementType={managementType}
                                deviceName={deviceData?.deviceName}
                                scope={priorityScope}
                                enabled={canChangePriority && !r.synthesized}
                                onChanged={onPriorityChanged}
                              /></span>
                            </div>
                            <div className="t247-wan-ip">{r.ip}</div>
                          </div>
                        </div>
                        <div className="t247-num">{fmt2(r.download)}</div>
                        <div className="t247-num">{fmt2(r.upload)}</div>
                        <div className="t247-num strong">{fmt2(r.total)}</div>
                        <div className="t247-sharecell">
                          <MiniBar pct={r.pct} color={r.isDown ? "rgba(255,255,255,.35)" : "#88F4EB"} />
                          <span className="t247-pct">{fmt1(r.pct)}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="t247-wan-row t247-wan-totals" style={{ cursor: "default" }}>
                    <div className="t247-eyebrow" style={{ padding: 0 }}>TOTALS</div>
                    <div className="t247-num strong">{fmt2(wanTotals.download)}</div>
                    <div className="t247-num strong">{fmt2(wanTotals.upload)}</div>
                    <div className="t247-num strong">{fmt2(wanTotals.total)}</div>
                    <div className="t247-num">100.0%</div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── DATA USAGE GRAPH + VLAN USAGE (replaces THROUGHPUT section) ─ */}
          <div className="t247-split2">
            <div className="t247-panel" data-tour="mon-wan-dist">
              <div className="t247-panel-header-row">
                <span>WAN USAGE DISTRIBUTION</span>
                <span className="t247-muted-label">{wanRows.length} INTERFACES</span>
              </div>
              {wanRows.length === 0 ? (
                <EmptyState text="No WAN usage data." />
              ) : (
                <div style={{ padding: 18, display: "flex", gap: 20 }}>
                  <div style={{ flex: 1 }}>
                    <DonutChart segments={wanDownloadSegments} label="DOWNLOAD · GB" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <DonutChart segments={wanUploadSegments} label="UPLOAD · GB" />
                  </div>
                </div>
              )}
            </div>

            <div className="t247-panel" data-tour="mon-vlan">
              <div className="t247-panel-header-row">
                <span>VLAN USAGE</span>
                <span className="sub">CLICK FOR CLIENT DETAIL</span>
                <span className="t247-muted-label">{deviceData.clientUsages.length} CLIENTS</span>
              </div>
              {vlanRows.length === 0 ? (
                <EmptyState text="No VLAN usage data." />
              ) : (
                vlanRows.map((v) => (
                  <div className="t247-vlan-row" key={v.vlan} onClick={() => setSelectedVlan(v)} role="button" tabIndex={0}>
                    <span className="t247-vlan-name">{v.vlan} · {v.name}</span>
                    <span className="t247-vlan-clients">{v.clients}c</span>
                    <MiniBar pct={v.pct} color={v.pct > 50 ? "#D7D7CC" : "#88F4EB"} />
                    <span className="t247-num" style={{ width: 74 }}>{fmt2(v.total)} GB</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* ── WAN DISTRIBUTION | CLIENT USAGE (added, same design language) ─ */}
          <div className="t247-split2">
            <div className="t247-panel" data-tour="mon-graph">
              <div className="t247-panel-header-row">
                <span>DATA USAGE GRAPH · {period.toUpperCase()}</span>
              </div>
              <div style={{ padding: 18 }}>
                {period === "daily" ? (
                  (rawDeviceData?.hourlyWanUsagesLogs?.details && Object.keys(rawDeviceData.hourlyWanUsagesLogs.details).length > 0) ||
                  (rawDeviceData?.dailyWanUsagesLogs?.details && Object.keys(rawDeviceData.dailyWanUsagesLogs.details).length > 0) ||
                  (rawDeviceData?.monthlyWanUsagesLogs?.details && Object.keys(rawDeviceData.monthlyWanUsagesLogs.details).length > 0) ? (
                    <CombinedHourlyGraph
                      hourlyWanUsagesLogs={rawDeviceData.hourlyWanUsagesLogs}
                      dailyWanUsagesLogs={rawDeviceData.dailyWanUsagesLogs}
                      monthlyWanUsagesLogs={rawDeviceData.monthlyWanUsagesLogs}
                    />
                  ) : (
                    <EmptyState text="No usage data." />
                  )
                ) : (
                  rawDeviceData?.dailyBreakdown?.length > 0 ? (
                    <DailyBreakdownChart
                      dailyBreakdown={rawDeviceData.dailyBreakdown}
                      period={period}
                    />
                  ) : (
                    <EmptyState text="No usage data." />
                  )
                )}
              </div>
            </div>

            <div className="t247-panel" data-tour="mon-clients">
              <div className="t247-panel-header-row">
                <span>CLIENT USAGE</span>
                <span className="sub" style={{ cursor: "pointer" }} onClick={() => setClientSortDesc((d) => !d)}>
                  SORT: TOTAL {clientSortDesc ? "↓" : "↑"}
                </span>
                <span className="t247-muted-label">{allClientsSorted.length} CLIENTS</span>
              </div>
              {allClientsSorted.length === 0 ? (
                <EmptyState text="No client usage data." />
              ) : (
                <div className="t247-client-list">
                  <div className="t247-wan-headrow" style={{ gridTemplateColumns: "1.6fr 70px 70px 70px 60px" }}>
                    <div>CLIENT</div>
                    <div style={{ textAlign: "right" }}>↓ GB</div>
                    <div style={{ textAlign: "right" }}>↑ GB</div>
                    <div style={{ textAlign: "right" }}>Σ GB</div>
                    <div style={{ textAlign: "right" }}>VLAN</div>
                  </div>
                  <div className="t247-scroll-block" style={{ maxHeight: 360 }}>
                    {allClientsSorted.slice(0, 100).map((c, i) => (
                      <div className="t247-wan-row" key={`${c.mac_address}-${i}`} style={{ gridTemplateColumns: "1.6fr 70px 70px 70px 60px", cursor: "default" }}>
                        <div className="t247-wan-name-cell">
                          <div>
                            <div className="t247-wan-name" style={{ fontSize: 12 }}>{c.name}</div>
                            <div className="t247-wan-ip">{c.ip_address}</div>
                          </div>
                        </div>
                        <div className="t247-num">{fmt2((c.download || 0) / 1024)}</div>
                        <div className="t247-num">{fmt2((c.upload || 0) / 1024)}</div>
                        <div className="t247-num strong">{fmt2((c.total_usage || 0) / 1024)}</div>
                        <div className="t247-num">{c.vlan}</div>
                      </div>
                    ))}
                  </div>
                  <div className="t247-wan-row t247-wan-totals" style={{ gridTemplateColumns: "1.6fr 70px 70px 70px 60px", cursor: "default" }}>
                    <div className="t247-eyebrow" style={{ padding: 0 }}>TOTALS</div>
                    <div className="t247-num strong">{fmt2(clientTotals.download)}</div>
                    <div className="t247-num strong">{fmt2(clientTotals.upload)}</div>
                    <div className="t247-num strong">{fmt2(clientTotals.total)}</div>
                    <div className="t247-num"></div>
                  </div>
                  {allClientsSorted.length > 100 && <div className="t247-footnote-row">SHOWING 100 OF {allClientsSorted.length} CLIENTS</div>}
                </div>
              )}
            </div>
          </div>

          {/* ── LOGS ────────────────────────────────────────────────────────── */}
          <div className="t247-panel" style={{ marginTop: 1 }} data-tour="mon-logs">
            <div className="t247-panel-header">LOGS &amp; ALERTS</div>
            <div style={{ padding: 14 }}>
              <LogsDisplay
                pepVPNLogs={deviceData?.pepVPNLogs}
                firewallLogs={deviceData?.firewallLogs}
                wanLogs={deviceData?.wanLogs}
                systemLogs={deviceData?.systemLogs}
                dhcpLogs={deviceData?.dhcpLogs}
                adminLogs={deviceData?.adminLogs}
                alerts={alerts}
                vlanAlerts={vlanAlerts}
                wanCapAlerts={wanCapAlerts}
                starlinkAlerts={starlinkAlerts}
                isClient={isClient}
                shouldSkipStarlink={shouldSkipStarlink}
                period={period}
              />
            </div>
            <div className="t247-log-footer">
              <span>*Times shown are the router's local timezone</span>
            </div>
          </div>

          {/* ── PING MONITORING (added, same design language) ─────────────── */}
          {/* 
          {pingStats && (
            <div className="t247-panel" style={{ marginTop: 1 }} data-tour="mon-ping">
              <div className="t247-panel-header-row">
                <span>PING MONITORING</span>
                <span className="t247-muted-label">{pingStats.totalChecks} CHECKS · {pingStats.targets.length} TARGETS</span>
              </div>
              <div style={{ padding: "16px 18px 0" }}>
                <div className="t247-throughput-legend">
                  <span>AVG LOSS {pingStats.avgPacketLoss != null ? fmt1(pingStats.avgPacketLoss) : "—"}%</span>
                </div>
                <AreaSparkWrap values={pingStats.rttSeries} label="AVG RTT PER CYCLE (MS)" />
              </div>
              <div className="t247-wan-headrow" style={{ gridTemplateColumns: "1.4fr 80px 80px 80px 80px 80px" }}>
                <div>TARGET</div>
                <div style={{ textAlign: "right" }}>CHECKS</div>
                <div style={{ textAlign: "right" }}>SUCCESS</div>
                <div style={{ textAlign: "right" }}>AVG LOSS</div>
                <div style={{ textAlign: "right" }}>AVG RTT</div>
                <div style={{ textAlign: "right" }}>MIN/MAX</div>
              </div>
              {pingStats.targets.map((t) => (
                <div className="t247-wan-row" key={t.target} style={{ gridTemplateColumns: "1.4fr 80px 80px 80px 80px 80px", cursor: "default" }}>
                  <div className="t247-wan-name" style={{ fontSize: 12 }}>{t.target}</div>
                  <div className="t247-num">{t.checks}</div>
                  <div className="t247-num">{fmt1(t.successRate)}%</div>
                  <div className="t247-num">{fmt1(t.avgLoss)}%</div>
                  <div className="t247-num">{t.avgRtt != null ? `${fmt1(t.avgRtt)}ms` : "—"}</div>
                  <div className="t247-num">{t.minRtt != null ? `${fmt1(t.minRtt)}/${fmt1(t.maxRtt)}` : "—"}</div>
                </div>
              ))}
            </div>
          )}
          */}

          {/* ── REAL-TIME BANDWIDTH (added, daily only) ────────────────────── */}
          {period === "daily" && (
            <div className="t247-panel" style={{ marginTop: 20 }} data-tour="mon-realtime">
              <div className="t247-panel-header-row">
                <span>REAL-TIME BANDWIDTH</span>
                <span className="t247-secondary-label">LIVE</span>
              </div>
              <div style={{ padding: 18 }}>
                <RealtimeBandwidthChart serialNumber={serialNumber} />
              </div>
            </div>
          )}

          {/* ── SPEEDFUSION VPN LIVE STATUS (added) ─────────────────────────
              Renders nothing on its own if the device has no SpeedFusion
              peers, so no extra guard is needed here. */}
          {period === "daily" && <SpeedFusionLiveStatus serialNumber={serialNumber} />}

          {/* ── LOCATION MAP (added) ──────────────────────────────────────────
              Peplink GPS history (track + playback) when the report has it;
              otherwise falls back to the Starlink/ELCOME live position
              (single approximate point, no playback). */}
          {(hasPeplinkTrack || starlinkLocation) && (
            <div className="t247-panel" style={{ marginTop: 20 }} data-tour="mon-map">
              <div className="t247-panel-header-row">
                <span>{hasPeplinkTrack ? "LOCATION TRACKING" : "LOCATION"}</span>
                <span className="t247-muted-label">
                  {hasPeplinkTrack ? "PEPLINK GPS · HISTORY" : "STARLINK · LIVE · APPROXIMATE"}
                </span>
              </div>
              <div style={{ padding: 18 }}>
                <LeafletMap
                  locationMap={deviceData.locationMap}
                  liveLocation={hasPeplinkTrack ? null : starlinkLocation}
                  isHistoricalView={!isLiveView}
                />
              </div>
            </div>
          )}
        </>
      )}

      {selectedWan && <T247WanModal row={selectedWan} deviceData={deviceData} onClose={() => setSelectedWan(null)} userType={userType} />}
      {selectedVlan && <T247VlanModal vlan={selectedVlan} onClose={() => setSelectedVlan(null)} />}

      <style dangerouslySetInnerHTML={{ __html: T247_THEME_CSS }} />
      <style dangerouslySetInnerHTML={{ __html: `
        /* Compatibility variables for the two embedded live components
           (RealtimeBandwidthChart, LeafletMap) which theme via var(--color-x)
           naming rather than the .t247-* classes used everywhere else here. */
        .t247 {
          --color-card: var(--navy-800);
          --color-background: var(--navy-800);
          --color-surface: var(--navy-700);
          --color-border: var(--line);
          --color-label: var(--text-muted);
          --color-text-primary: #fff;
          --color-text-secondary: var(--text-dim);
          --color-primary: var(--navy-800);
          --color-download: #d7d7cc;
          --color-secondary: var(--turquoise);
          --color-upload: var(--turquoise);
          --color-success: var(--turquoise);
        }

        .t247-live-badge {
          display: flex; align-items: center; gap: 7px; padding: 5px 10px; background: rgba(136, 244, 235, 0.14);
          font: 400 10px var(--font-mono); letter-spacing: 0.1em; color: var(--turquoise); white-space: nowrap;
        }
        .t247-live-badge.is-off { background: rgba(255, 255, 255, 0.1); color: var(--text-dim); }
        .t247-dot { width: 6px; height: 6px; background: currentColor; border-radius: 999px; display: block; }

        .t247-back-btn {
          display: inline-flex; align-items: center; gap: 8px; margin-bottom: 12px; padding: 7px 12px;
          font: 400 11px var(--font-mono); letter-spacing: 0.06em; color: var(--text-dim);
          background: transparent; border: 1px solid rgba(255, 255, 255, 0.16); cursor: pointer;
        }
        .t247-back-btn:hover { color: var(--turquoise); border-color: var(--turquoise); }
        .t247-back-btn:focus-visible { outline: 2px solid var(--turquoise); outline-offset: 2px; }

        .t247-devicebar {
          display: flex; align-items: flex-end; justify-content: space-between; gap: 28px; padding: 22px 24px 18px;
          background: var(--navy-800); border-bottom: 1px solid var(--line); flex-wrap: wrap;
        }
        .t247-devicename { margin-top: 6px; font-weight: 700; font-size: 26px; line-height: 1.1; font-family: var(--font-sans); letter-spacing: -0.02em; }
        .t247-devicesub { margin-top: 8px; display: flex; gap: 10px; flex-wrap: wrap; font: 400 11px var(--font-mono); color: var(--text-dim); }
        .t247-devicebar-right { display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap; }
        .t247-period-toggle { display: flex; margin-top: 6px; border: 1px solid rgba(255, 255, 255, 0.16); }
        .t247-period-btn { padding: 9px 14px; font: 400 11px var(--font-mono); letter-spacing: 0.06em; color: var(--text-dim); background: transparent; border: none; cursor: pointer; }
        .t247-period-btn.active { background: var(--turquoise); color: var(--navy-800); }

        .t247-error {
          margin: 24px; padding: 24px; text-align: center; color: var(--text-dim); font: 400 12px var(--font-mono);
          background: var(--navy-800); border: 1px solid var(--line);
        }
        @keyframes t247spin { to { transform: rotate(360deg); } }

        .t247-columns { display: grid; grid-template-columns: 340px 1fr; gap: 1px; background: var(--line); }
        .t247-leftcol { display: flex; flex-direction: column; gap: 1px; background: var(--line); }

        .t247-panel-header { padding: 14px 18px; border-bottom: 1px solid var(--line); font: 400 10px var(--font-mono); letter-spacing: 0.14em; color: var(--text-dim); }
        .t247-panel-header.sub { margin-top: 4px; border-top: 1px solid var(--line); }

        .t247-state-grid { display: grid; grid-template-columns: 1fr 1fr; }
        .t247-state-cell { padding: 14px 18px; border-bottom: 1px solid rgba(255, 255, 255, 0.08); }
        .t247-state-cell:nth-child(odd) { border-right: 1px solid rgba(255, 255, 255, 0.08); }
        .t247-state-cell .label { font: 400 9.5px var(--font-mono); letter-spacing: 0.12em; color: var(--text-muted); }
        .t247-state-cell .value { margin-top: 5px; font: 400 14px var(--font-mono); }
        .t247-state-cell .value.small { font-size: 12.5px; }

        .t247-glance-list { padding: 0 18px 16px; }
        .t247-glance-row { display: flex; align-items: baseline; gap: 10px; padding: 12px 0; border-bottom: 1px solid rgba(255, 255, 255, 0.08); }
        .t247-glance-row.last { border-bottom: none; }
        .t247-glance-row .glabel { font: 400 11.5px var(--font-sans); color: var(--text-dim); flex: 1; }
        .t247-glance-row .gvalue { font: 700 16px var(--font-mono); }
        .t247-glance-row .gunit { font-size: 11px; font-weight: 400; color: var(--text-muted); margin-left: 4px; }

        .t247-flagged { margin: 0; padding: 14px 18px; background: var(--navy-700); border-left: 2px solid var(--neon); }
        .t247-flagged-text { margin-top: 7px; font-weight: 400; font-size: 12px; line-height: 1.55; font-family: var(--font-sans); }

        .t247-wan-headrow, .t247-wan-row {
          display: grid; grid-template-columns: 1.6fr 88px 88px 88px 1fr; align-items: center; gap: 14px; padding: 11px 18px;
        }
        .t247-wan-headrow { background: var(--navy-700); border-bottom: 1px solid var(--line); font: 400 9.5px var(--font-mono); letter-spacing: 0.12em; color: var(--text-dim); }
        .t247-wan-row { border-bottom: 1px solid rgba(255, 255, 255, 0.08); cursor: pointer; }
        .t247-wan-row:hover { background: rgba(255, 255, 255, 0.03); }
        .t247-wan-name-cell { display: flex; align-items: center; gap: 11px; }
        .t247-wan-dot { width: 7px; height: 7px; flex: none; }
        .t247-wan-name { font: 700 13px var(--font-sans); display: flex; align-items: center; gap: 7px; }
        .t247-wan-ip { margin-top: 2px; font: 400 10.5px var(--font-mono); color: var(--text-muted); }
        .t247-sharecell { display: flex; align-items: center; gap: 10px; }
        .t247-pct { font: 400 12px var(--font-mono); width: 44px; text-align: right; }
        .t247-wan-totals { background: var(--navy-700); font-weight: 700; }
        .t247-footnote-row { padding: 12px 18px; font: 400 10px var(--font-mono); letter-spacing: 0.08em; color: var(--text-muted); text-align: center; }
        .t247-scroll-block { overflow-y: auto; }
        .t247-scroll-block::-webkit-scrollbar { width: 6px; }
        .t247-scroll-block::-webkit-scrollbar-track { background: transparent; }
        .t247-scroll-block::-webkit-scrollbar-thumb { background: rgba(136, 244, 235, 0.25); }
        .t247-scroll-block::-webkit-scrollbar-thumb:hover { background: rgba(136, 244, 235, 0.4); }

        .t247-split2, .t247-bottom-split { display: grid; gap: 1px; background: var(--line); margin-top: 1px; }
        .t247-split2 { grid-template-columns: 1fr 1fr; }
        .t247-bottom-split { grid-template-columns: 1fr 460px; }

        .t247-throughput-legend { display: flex; gap: 16px; padding: 12px 18px 0; font: 400 12px var(--font-mono); }
        .t247-throughput-legend .muted { color: var(--text-dim); }
        .t247-chart-swatches { display: flex; gap: 16px; padding: 8px 18px 18px; font: 400 10px var(--font-mono); letter-spacing: 0.06em; color: var(--text-dim); }
        .t247-chart-swatches span { display: flex; align-items: center; gap: 6px; }
        .t247-chart-swatches i { width: 9px; height: 9px; display: block; }

        .t247-talker-row, .t247-vlan-row { display: flex; align-items: center; gap: 12px; padding: 9px 18px; border-bottom: 1px solid rgba(255, 255, 255, 0.08); cursor: pointer; }
        .t247-talker-row { cursor: default; }
        .t247-vlan-row:hover { background: rgba(255, 255, 255, 0.03); }
        .t247-talker-name { flex: 1; font: 700 12px var(--font-sans); }
        .t247-talker-name .muted { font-weight: 400; font-family: var(--font-mono); font-size: 10.5px; color: var(--text-muted); }
        .t247-vlan-name { width: 140px; font: 700 12px var(--font-sans); }
        .t247-vlan-clients { width: 30px; font: 400 11px var(--font-mono); color: var(--text-muted); }

        .t247-statbox-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1px; background: var(--line); margin: 14px 18px 0; border: 1px solid var(--line); }
        .t247-statbox { background: var(--navy-800); padding: 13px 15px; }
        .t247-statbox .label { font: 400 9.5px var(--font-mono); letter-spacing: 0.12em; color: var(--text-muted); }
        .t247-statbox .value { margin-top: 6px; font: 700 22px var(--font-mono); }
        .t247-statbox .value.small { font-size: 15px; }
        .t247-statbox .unit { font-size: 11px; font-weight: 400; color: var(--text-muted); margin-left: 4px; }

        .t247-logs-tabs { display: flex; gap: 2px; padding: 14px 18px 0; }
        .t247-logs-tab { padding: 8px 13px; font: 400 10.5px var(--font-mono); letter-spacing: 0.08em; color: var(--text-dim); background: transparent; border: none; cursor: pointer; }
        .t247-logs-tab.active { background: var(--turquoise); color: var(--navy-800); }
        .t247-log-list { margin-top: 12px; border-top: 1px solid rgba(255, 255, 255, 0.08); }
        .t247-log-row { display: flex; gap: 12px; padding: 11px 18px; border-bottom: 1px solid rgba(255, 255, 255, 0.08); }
        .t247-log-time { font: 400 10.5px var(--font-mono); color: var(--text-muted); padding-top: 1px; flex: none; }
        .t247-log-level { padding: 1px 6px; height: 15px; background: rgba(255, 255, 255, 0.13); font: 400 9px var(--font-mono); letter-spacing: 0.08em; color: var(--text-dim); flex: none; }
        .t247-log-level.warn { background: var(--neon); color: var(--navy-800); }
        .t247-log-text { flex: 1; font-weight: 400; font-size: 11.5px; line-height: 1.5; font-family: var(--font-mono); }
        .t247-log-footer { display: flex; align-items: center; justify-content: space-between; padding: 12px 18px; font: 400 9.5px var(--font-mono); letter-spacing: 0.05em; color: var(--text-muted); }

        @media (max-width: 1100px) {
          .t247-columns, .t247-split2, .t247-bottom-split { grid-template-columns: 1fr; }
          .t247-leftcol { flex-direction: column; }
          .t247-devicebar { flex-direction: column; align-items: flex-start; }
        }
      ` }} />
    </div>
  );
}

// ── Small presentational helpers ─────────────────────────────────────────────
const StateCell = ({ label, value, small }) => (
  <div className="t247-state-cell">
    <div className="label">{label}</div>
    <div className={`value ${small ? "small" : ""}`}>{value}</div>
  </div>
);

const GlanceRow = ({ label, value, unit, last }) => (
  <div className={`t247-glance-row ${last ? "last" : ""}`}>
    <span className="glabel">{label}</span>
    <span className="gvalue">
      {value} {unit && <span className="gunit">{unit}</span>}
    </span>
  </div>
);

const StatBox = ({ label, value, unit, small }) => (
  <div className="t247-statbox">
    <div className="label">{label}</div>
    <div className={`value ${small ? "small" : ""}`}>
      {value} {unit && <span className="unit">{unit}</span>}
    </div>
  </div>
);

const AreaSparkWrap = ({ values, label }) => (
  <div style={{ marginTop: 10 }}>
    <div className="t247-eyebrow" style={{ marginBottom: 6 }}>{label}</div>
    <AreaSpark values={values} height={80} color="#88F4EB" />
  </div>
);

const EmptyState = ({ text }) => <div className="t247-empty">{text}</div>;

export default DesktopLayout;
